// src/pages/KotaForm.jsx

import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Modal,
  TextInput,
  Button,
  LoadingOverlay,
  Alert,
  Group,
} from "@mantine/core";
// Impor 'api' service kita
import api from "../../services/api";

/**
 * Komponen ini adalah Modal Form untuk 'Tambah' dan 'Edit' Kota.
 *
 * Props:
 * - opened: boolean (Apakah modal terbuka?)
 * - onClose: function (Dipanggil saat modal ditutup)
 * - onSuccess: function (Dipanggil setelah simpan sukses)
 * - kota: object | null (Data kota jika mode 'Edit', null jika mode 'Tambah')
 */
export default function KotaForm({ opened, onClose, onSuccess, kota }) {
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Tentukan apakah ini mode "Edit"
  // Jika 'kota' (props) berisi data, maka 'isEdit' = true
  const isEdit = Boolean(kota); 

  // Inisialisasi Mantine Form
  const form = useForm({
    initialValues: {
      nama_kota: "",
    },
    validate: {
      // Validasi sederhana: nama_kota tidak boleh kosong
      nama_kota: (val) => (val.trim().length === 0 ? "Nama kota tidak boleh kosong" : null),
    },
  });

  // useEffect ini "mengawasi" props 'kota' dan 'opened'.
  // Ini penting agar form terisi data yang benar saat mode 'Edit'.
  useEffect(() => {
    if (opened) {
      if (isEdit) {
        // Jika mode Edit, isi form dengan data dari 'kota'
        form.setValues({ nama_kota: kota.nama_kota });
      } else {
        // Jika mode Tambah, pastikan form kosong
        form.reset();
      }
    }
  }, [kota, opened]); // '[]' dependency

  // Fungsi saat tombol "Batal" atau "X" di modal ditekan
  const handleClose = () => {
    form.reset(); // Kosongkan form
    setFormError(null); // Bersihkan error
    onClose(); // Tutup modal (fungsi dari parent)
  };

  // Fungsi saat tombol "Simpan" (submit) ditekan
  const handleSubmit = async (values) => {
    setLoading(true);
    setFormError(null);
    
    try {
      if (isEdit) {
        // --- PROSES EDIT ---
        // Panggil API 'update' (PUT) dari WilayahController
        await api.put(`/admin/kota/${kota.id}`, values);
      } else {
        // --- PROSES TAMBAH BARU ---
        // Panggil API 'store' (POST) dari WilayahController
        await api.post("/admin/kota", values);
      }
      
      // Jika sukses:
      onSuccess(); // Panggil callback sukses (untuk refresh tabel di parent)
      handleClose(); // Tutup modal dan reset form

    } catch (err) {
      // Tangani error
      if (err.response && err.response.status === 422) {
        // Error Validasi (misal: nama_kota 'unique' Gagal)
        const errors = err.response.data.errors;
        setFormError(Object.values(errors).flat().join(", "));
      } else {
        // Error Server lainnya
        setFormError("Gagal menyimpan data. Coba lagi.");
      }
    } finally {
      setLoading(false); // Hentikan loading
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      // Judul modal berubah tergantung mode
      title={isEdit ? "Edit Kota" : "Tambah Kota Baru"}
      size="md"
      overlayProps={{ blur: 2 }}
    >
      {/* Tampilkan overlay loading saat submitting */}
      <LoadingOverlay visible={loading} />
      
      <form onSubmit={form.onSubmit(handleSubmit)}>
        
        {/* Tampilkan pesan error jika ada */}
        {formError && (
          <Alert 
            color="red" 
            title="Error" 
            withCloseButton 
            onClose={() => setFormError(null)} 
            mb="md"
          >
            {formError}
          </Alert>
        )}
        
        <TextInput
          label="Nama Kota"
          placeholder="Contoh: Kota Bogor"
          {...form.getInputProps("nama_kota")}
          required
        />

        <Group justify="flex-end" mt="xl">
          <Button variant="default" onClick={handleClose}>
            Batal
          </Button>
          <Button type="submit" loading={loading}>
            Simpan
          </Button>
        </Group>
      </form>
    </Modal>
  );
}